import { useCallback, useEffect, useRef, useState } from 'react';

import type { IdleWatchSeam } from './hostDeps';
import type { LoggerDispatch } from './state/loggerDraft';
import type { DraftEntry, LoggerAction } from './state/loggerDraftTypes';
import { latestCompletedAtMs } from './timelinePlacement';
import type { AutoSaveResult } from './useSaveWorkout';

// #445, Antenore 23/10: a workout left open. After 45 minutes with nothing
// done, the host asks «still training?». No answer in 15 minutes: the
// workout is saved and closed (autoSave). Nothing done = no completed set,
// no change of the athlete's to the draft, no timer ending or paused, no
// «Continue», since that long.
// A countdown (rest, hold) does not switch the watch off: in the background
// its JS never runs out, and the question would never come. Its end counts
// instead. A count-up (cardio) has no end: while it runs, the watch is off
// (read off its state, not a timeout), and its stop or pause counts.

export const IDLE_ASK_MS = 45 * 60 * 1000;
export const IDLE_ANSWER_MS = 15 * 60 * 1000;
// Typing moves the clock once a minute, not at each key.
const TAP_STEP_MS = 60 * 1000;

// Writes no tap of the athlete's makes. The persistence and records hooks
// get the plain dispatch; these are its own writes, should one come through.
const AUTOMATIC: ReadonlySet<LoggerAction['type']> = new Set([
  'hydrate',
  'reset',
  'complete_assisted',
]);

/** The latest moment something happened besides a completed set, kept as a
 *  max. `dispatch` moves it on every change of the athlete's to the draft. */
export function useActivityClock(rawDispatch: LoggerDispatch): {
  activityAt: number;
  bump: (atMs: number, tap?: boolean) => void;
  dispatch: LoggerDispatch;
} {
  const [activityAt, setActivityAt] = useState(0);
  const atRef = useRef(0);
  const bump = useCallback((atMs: number, tap = false) => {
    if (atMs <= atRef.current) return;
    if (tap && atMs - atRef.current < TAP_STEP_MS) return;
    atRef.current = atMs;
    setActivityAt(atMs);
  }, []);
  const dispatch = useCallback<LoggerDispatch>(
    (action) => {
      if (!AUTOMATIC.has(action.type)) bump(Date.now(), true);
      rawDispatch(action);
    },
    [rawDispatch, bump],
  );
  return { activityAt, bump, dispatch };
}

type TimerReading = {
  endsAt: number | null;
  startedAt?: number | null;
  mode?: 'up' | 'down';
  isPaused: boolean;
};

export function useIdleWatch({
  armed,
  entries,
  startedAt,
  activity,
  rest,
  hold,
  submitting,
  seam,
  autoSave,
}: {
  /** A live workout is in the logger, shown or minimized. */
  armed: boolean;
  entries: readonly DraftEntry[];
  startedAt: string;
  activity: { activityAt: number; bump: (atMs: number) => void };
  rest: TimerReading;
  hold: TimerReading;
  /** A save is running: no question meanwhile, and again once it is done. */
  submitting: boolean;
  seam: IdleWatchSeam | undefined;
  autoSave: () => Promise<AutoSaveResult>;
}): void {
  const { activityAt, bump } = activity;
  // A countdown counts at its end, a pause when it is taken, a count-up when
  // it stops (below). startedAt is null while paused.
  const restMark = rest.endsAt;
  const holdMark = hold.endsAt;
  const countUpRunning = hold.mode === 'up' && hold.startedAt != null && !hold.isPaused;
  const paused = rest.isPaused || hold.isPaused;
  useEffect(() => {
    if (restMark !== null) bump(restMark);
  }, [restMark, bump]);
  useEffect(() => {
    if (holdMark !== null) bump(holdMark);
  }, [holdMark, bump]);
  useEffect(() => {
    if (paused) bump(Date.now());
  }, [paused, bump]);
  const wasCountUp = useRef(false);
  useEffect(() => {
    if (wasCountUp.current && !countUpRunning) bump(Date.now());
    wasCountUp.current = countUpRunning;
  }, [countUpRunning, bump]);

  const since = Math.max(latestCompletedAtMs(entries), Date.parse(startedAt) || 0, activityAt);
  const live = seam !== undefined && armed && !submitting && !countUpRunning;
  // An answer taken: the question is set again from what is true now.
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (seam === undefined) return;
    if (!live) {
      void seam.cancel();
      return;
    }
    // Already past (a draft resumed hours later): ask now.
    const askAt = Math.max(since + IDLE_ASK_MS, Date.now() + 1000);
    void seam.schedule(askAt, askAt + IDLE_ANSWER_MS);
  }, [seam, live, since, round]);

  useEffect(
    () => () => {
      void seam?.cancel();
    },
    [seam],
  );

  const latest = useRef({ live, autoSave, startedAt, bump });
  latest.current = { live, autoSave, startedAt, bump };
  useEffect(() => {
    if (seam === undefined) return undefined;
    return seam.onAnswer(async (answer) => {
      const cur = latest.current;
      try {
        // Also before the workout is back in the logger (a cold start).
        if (answer === 'continue') {
          cur.bump(Date.now());
          return;
        }
        // A question this workout no longer has.
        if (!cur.live) return;
        const result = await cur.autoSave();
        if (result.kind === 'closed') seam.onClosed(result.sessionId, cur.startedAt);
      } finally {
        setRound((n) => n + 1);
      }
    });
  }, [seam]);
}
